/**
 * Capacidades SaaS — fuente única frontend (espejo de backend/membership_capabilities.py).
 * Importar desde aquí en código nuevo; @/lib/billing/membershipCapabilityMatrix re-exporta por compatibilidad.
 */

export const CAPABILITY_KEYS = [
  'public_menu',
  'dashboard_access',
  'orders',
  'assistant',
  'analytics',
  'custom_domain',
  'advanced_promos',
  'campaigns',
  'branding_removal',
  'team',
  'wallet',
  'loyalty',
  'multi_store',
];

export const MODULE_CAPABILITY_KEYS = [
  'module_pos',
  'module_kds',
  'module_orders',
  'module_assistant',
  'module_analytics',
  'module_club',
  'module_solugo',
  'module_reports',
  'module_payments',
];

const MODULE_MENU = {
  module_pos: false,
  module_kds: false,
  module_orders: false,
  module_assistant: false,
  module_analytics: true,
  module_club: false,
  module_solugo: false,
  module_reports: false,
  module_payments: false,
};

const MODULE_VENTA = {
  module_pos: false,
  module_kds: false,
  module_orders: true,
  module_assistant: true,
  module_analytics: true,
  module_club: false,
  module_solugo: false,
  module_reports: false,
  module_payments: false,
};

const MODULE_STARTER = {
  module_pos: true,
  module_kds: true,
  module_orders: true,
  module_assistant: true,
  module_analytics: true,
  module_club: false,
  module_solugo: true,
  module_reports: false,
  module_payments: false,
};

const MODULE_PRO = {
  ...MODULE_STARTER,
  module_club: true,
  module_solugo: true,
  module_payments: true,
};

export const MODULE_PLAN_CAPABILITY_MATRIX = {
  menu: { ...MODULE_MENU },
  venta: { ...MODULE_VENTA },
  starter: { ...MODULE_STARTER },
  pro: { ...MODULE_PRO },
  business: { ...MODULE_PRO, module_reports: true },
};

const MENU_ACTIVE = {
  public_menu: true,
  dashboard_access: true,
  orders: false,
  assistant: false,
  analytics: true,
  advanced_promos: false,
  campaigns: false,
  branding_removal: false,
  custom_domain: false,
  team: false,
  wallet: false,
  loyalty: false,
  multi_store: false,
};

const VENTA_ACTIVE = {
  public_menu: true,
  dashboard_access: true,
  orders: true,
  assistant: true,
  analytics: true,
  advanced_promos: false,
  campaigns: false,
  branding_removal: false,
  custom_domain: false,
  team: false,
  wallet: false,
  loyalty: false,
  multi_store: false,
};

const STARTER_ACTIVE = {
  public_menu: true,
  dashboard_access: true,
  orders: true,
  assistant: true,
  analytics: true,
  advanced_promos: false,
  campaigns: false,
  branding_removal: false,
  custom_domain: false,
  team: false,
  wallet: true,
  loyalty: false,
  multi_store: false,
};

const PRO_ACTIVE = {
  public_menu: true,
  dashboard_access: true,
  orders: true,
  assistant: true,
  analytics: true,
  custom_domain: true,
  advanced_promos: true,
  campaigns: true,
  branding_removal: true,
  team: true,
  wallet: true,
  loyalty: true,
  multi_store: false,
};

const PRO_TRIAL_ACTIVE = {
  ...PRO_ACTIVE,
  custom_domain: false,
};

export const PLAN_CAPABILITY_MATRIX = {
  menu: { ...MENU_ACTIVE },
  venta: { ...VENTA_ACTIVE },
  starter: { ...STARTER_ACTIVE },
  pro: { ...PRO_ACTIVE },
  business: { ...PRO_ACTIVE, multi_store: true },
};

export const UI_FEATURE_TO_CAPABILITY = {
  custom_domain: 'custom_domain',
  branding: 'branding_removal',
  analytics: 'analytics',
  campaigns: 'campaigns',
  advanced_promos: 'advanced_promos',
  assistant: 'assistant',
  orders: 'orders',
  whatsapp_orders: 'orders',
  team: 'team',
  wallet: 'wallet',
  loyalty: 'loyalty',
  multi_store: 'multi_store',
};

export function normalizeSaasPlanId(planId) {
  const raw = String(planId || '').trim().toLowerCase();
  if (raw === 'premium') return 'pro';
  if (raw === 'menu' || raw === 'venta' || raw === 'starter' || raw === 'pro' || raw === 'business') {
    return raw;
  }
  if (raw === 'emprendedor') return 'starter';
  if (raw === 'profesional') return 'pro';
  return null;
}

function inactiveCaps() {
  return {
    public_menu: true,
    dashboard_access: true,
    orders: true,
    assistant: false,
    analytics: false,
    custom_domain: false,
    advanced_promos: false,
    campaigns: false,
    branding_removal: false,
    team: false,
    wallet: false,
    loyalty: false,
    multi_store: false,
  };
}

function softSuspendedCaps() {
  return { ...inactiveCaps() };
}

function emptyModules() {
  return Object.fromEntries(MODULE_CAPABILITY_KEYS.map((key) => [key, false]));
}

function moduleBaseForPlan(planId) {
  const pid = normalizeSaasPlanId(planId) || 'starter';
  if (pid === 'business') return { ...MODULE_PRO, module_reports: true };
  if (pid === 'pro') return { ...MODULE_PRO };
  if (pid === 'menu') return { ...MODULE_MENU };
  if (pid === 'venta') return { ...MODULE_VENTA };
  return { ...MODULE_STARTER };
}

export function moduleCapabilitiesForPlan(planId, accessState = 'active', capabilitySnapshot = null) {
  const st = String(accessState || 'active').trim().toLowerCase();
  const pid = normalizeSaasPlanId(planId) || 'starter';
  const caps =
    capabilitySnapshot && typeof capabilitySnapshot === 'object'
      ? capabilitySnapshot
      : membershipCapabilitiesForPlan(planId, accessState);

  if (st === 'soft_suspended' || st === 'inactive') {
    return emptyModules();
  }

  const base = moduleBaseForPlan(pid);
  if (!caps.analytics) {
    base.module_analytics = false;
    base.module_reports = false;
  }
  if (!caps.assistant) {
    base.module_assistant = false;
  }
  if (!caps.loyalty) {
    base.module_club = false;
  }
  if (!caps.wallet) {
    base.module_solugo = false;
  }
  return base;
}

export function membershipCapabilitiesForPlan(planId, accessState = 'active') {
  const st = String(accessState || 'active').trim().toLowerCase();
  const pid = normalizeSaasPlanId(planId) || 'starter';

  if (st === 'soft_suspended') return softSuspendedCaps();
  if (st === 'inactive') return inactiveCaps();
  if (st === 'pending_verification') {
    if (pid === 'pro') return { ...PRO_ACTIVE };
    if (pid === 'menu') return { ...MENU_ACTIVE };
    if (pid === 'venta') return { ...VENTA_ACTIVE };
    return { ...STARTER_ACTIVE };
  }
  if (st === 'trial') {
    if (pid === 'business') return { ...PRO_ACTIVE, multi_store: true };
    if (pid === 'pro') return { ...PRO_TRIAL_ACTIVE };
    if (pid === 'menu') return { ...MENU_ACTIVE };
    if (pid === 'venta') return { ...VENTA_ACTIVE };
    return { ...STARTER_ACTIVE };
  }
  if (st === 'beta') return { ...PRO_ACTIVE };
  if (st === 'expired') {
    if (pid === 'menu') return { ...MENU_ACTIVE };
    if (pid === 'venta') return { ...VENTA_ACTIVE };
    return { ...STARTER_ACTIVE };
  }
  if (st === 'active' || st === 'expiring_soon' || st === 'grace_period') {
    if (pid === 'business') return { ...PRO_ACTIVE, multi_store: true };
    if (pid === 'pro') return { ...PRO_ACTIVE };
    if (pid === 'menu') return { ...MENU_ACTIVE };
    if (pid === 'venta') return { ...VENTA_ACTIVE };
    return { ...STARTER_ACTIVE };
  }
  if (pid === 'business') return { ...PRO_ACTIVE, multi_store: true };
  if (pid === 'pro') return { ...PRO_ACTIVE };
  if (pid === 'menu') return { ...MENU_ACTIVE };
  if (pid === 'venta') return { ...VENTA_ACTIVE };
  return { ...STARTER_ACTIVE };
}

export function hasPlanCapability(planId, capabilityKey, accessState = 'active') {
  const key = String(capabilityKey || '').trim().toLowerCase();
  const caps = membershipCapabilitiesForPlan(planId, accessState);
  return Boolean(caps[key]);
}
